import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import {
  assignMembershipRole,
  changeMembershipRole,
  reactivateMembership,
  removeMembership,
  revokeMembershipRole,
  suspendMembership,
  type Member,
  type MembershipRole,
} from './api';
import type { MemberActionKind } from './MembersTable';

export interface MemberAction {
  kind: MemberActionKind;
  member: Member;
}

const ACTION_TOAST: Record<MemberActionKind, string> = {
  suspend: 'toasts.member_suspended',
  remove: 'toasts.member_removed',
  reactivate: 'toasts.member_reactivated',
};

/** Role and status mutations for the Members page, with cache invalidation. */
export function useMemberMutations(onActionDone: () => void) {
  const { t } = useTranslation('members');
  const queryClient = useQueryClient();
  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['memberships'] });
  const fail = (error: unknown): void => {
    toast.error(t(parseApiError(error).i18nKey));
  };

  const roleMutation = useMutation({
    mutationFn: ({ membershipId, role }: { membershipId: string; role: MembershipRole }) =>
      changeMembershipRole(membershipId, role),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.role_updated'));
    },
    onError: fail,
  });

  const assignRoleMutation = useMutation({
    mutationFn: ({ membershipId, roleId }: { membershipId: string; roleId: string }) =>
      assignMembershipRole(membershipId, roleId),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.role_assigned'));
    },
    onError: fail,
  });

  const revokeRoleMutation = useMutation({
    mutationFn: ({ membershipId, roleId }: { membershipId: string; roleId: string }) =>
      revokeMembershipRole(membershipId, roleId),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.role_revoked'));
    },
    onError: fail,
  });

  const actionMutation = useMutation({
    mutationFn: async ({ kind, member }: MemberAction) => {
      if (kind === 'suspend') return suspendMembership(member.id);
      if (kind === 'remove') return removeMembership(member.id);
      return reactivateMembership(member.id);
    },
    onSuccess: async (_, action) => {
      await invalidate();
      toast.success(t(ACTION_TOAST[action.kind]));
      onActionDone();
    },
    onError: fail,
  });

  return {
    changeRole: (membershipId: string, role: MembershipRole) =>
      roleMutation.mutate({ membershipId, role }),
    assignRole: (membershipId: string, roleId: string) =>
      assignRoleMutation.mutate({ membershipId, roleId }),
    revokeRole: (membershipId: string, roleId: string) =>
      revokeRoleMutation.mutate({ membershipId, roleId }),
    runAction: (action: MemberAction) => actionMutation.mutate(action),
    roleBusy: roleMutation.isPending,
    rolesBusy: assignRoleMutation.isPending || revokeRoleMutation.isPending,
    actionBusy: actionMutation.isPending,
  };
}
